import {MapControllerInput, Marker, MapInitOptions} from '../interfaces';
import {VisibilityManager} from './visibility.manager';
import {MapLayersController, MapDrawingController, MapEventsController} from '../openstreetmap.controller';

export interface OsmMapControllerInputContext {
  isMapInitialized: boolean;
  mapInstance: any;
  layersController: MapLayersController | undefined;
  drawingController: MapDrawingController | undefined;
  eventsController: MapEventsController | undefined;
  mapOptions: MapInitOptions;
  visibilityManager: VisibilityManager;
}

/**
 * Applies {@link MapControllerInput} commands to Leaflet controllers (markers, polygons, drawing, layers).
 */
export class OsmMapControllerInputManager {
  constructor(private readonly getCtx: () => OsmMapControllerInputContext) {}

  process(input: MapControllerInput): void {
    const ctx = this.getCtx();

    if (input.initialize) {
      /* applyMapOptions is owned by host */
    }

    if (input.addCustomLayer && ctx.layersController) {
      ctx.layersController.addCustomTileLayer(
        input.addCustomLayer.id,
        input.addCustomLayer.getUrl,
        input.addCustomLayer.options,
      );
    }

    if (input.removeCustomLayer && ctx.layersController) {
      ctx.layersController.removeCustomLayer(input.removeCustomLayer.id);
    }

    if (input.addMarkers) {
      this.addMarkersToMap(ctx, input.addMarkers);
    }
    if (input.addMarker) {
      this.addSingleMarker(ctx, input.addMarker);
    }
    if (input.mergeMarkers?.length) {
      this.mergeMarkersToMap(ctx, input.mergeMarkers);
    }
    if (input.removeMarker) {
      this.removeMarkerFromMap(ctx, input.removeMarker.id);
    }
    if (input.removeMarkers) {
      this.removeMarkersFromMap(ctx, input.removeMarkers);
    }
    if (input.removePolygons) {
      input.removePolygons.forEach(markerId => this.removePolygonFromMap(ctx, markerId));
    }
    if (input.clearMarkers) {
      this.clearAllMarkers(ctx);
    }
    if (input.setZoom && ctx.mapInstance) {
      ctx.mapInstance.setZoom(input.setZoom);
    }

    if (input.addPolygon) {
      this.addPolygonToMap(ctx, input.addPolygon.coordinates, input.addPolygon.properties);
    }
    if (input.addPolygons) {
      input.addPolygons.forEach(polygon => {
        this.addPolygonToMap(ctx, polygon.coordinates, polygon.properties);
      });
    }

    if (input.addGisPolygon) {
      this.addGisPolygonToMap(ctx, input.addGisPolygon);
    }
    if (input.addGisPolygons) {
      this.addGisPolygonsToMap(ctx, input.addGisPolygons);
    }

    if (input.removePolygon) {
      this.removePolygonFromMap(ctx, input.removePolygon.id);
    }

    if (input.updatePolygon) {
      this.updatePolygonOnMap(
        ctx,
        input.updatePolygon.id,
        input.updatePolygon.coordinates,
        input.updatePolygon.properties,
      );
    }

    if (input.clearPolygons) {
      this.clearAllPolygons(ctx);
    }

    if (input.addCircleMarker && ctx.layersController) {
      ctx.layersController.addCircleMarker(input.addCircleMarker);
    }
    if (input.addCircleMarkers && ctx.layersController) {
      ctx.layersController.addCircleMarkers(input.addCircleMarkers);
    }
    if (input.removeCircleMarker && ctx.layersController) {
      ctx.layersController.removeCircleMarker(input.removeCircleMarker.id);
    }
    if (input.clearCircleMarkers && ctx.layersController) {
      ctx.layersController.clearCircleMarkers();
    }

    if (input.setCenter) {
      this.setCenterOnMap(ctx, input.setCenter?.lat, input.setCenter.lng, input.setCenter.zoom);
    }
    if (input.fitBounds) {
      this.fitBoundsToMarkers(ctx, input.fitBounds);
    }

    if (input.enableRectangleDrawing && ctx.drawingController) {
      ctx.drawingController.enableRectangleDrawing();
    }
    if (input.disableRectangleDrawing && ctx.drawingController) {
      ctx.drawingController.disableRectangleDrawing();
    }
    if (input.enablePolylineDrawing && ctx.drawingController) {
      ctx.drawingController.enablePolylineDrawing();
    }
    if (input.disablePolylineDrawing && ctx.drawingController) {
      ctx.drawingController.disablePolylineDrawing();
    }

    if (input.setBaseMap) {
      /* host stub */
    }
    if (input.addOSMOverlay) {
      /* host stub */
    }
    if (input.removeOSMOverlay) {
      /* host stub */
    }

    if (input?.setShowRegionNames !== undefined) {
      ctx.mapOptions.showRegionNames = input.setShowRegionNames;
      ctx.layersController?.setShowRegionNames(input.setShowRegionNames);
      ctx.visibilityManager.syncShowRegionNamesFromExternal(input.setShowRegionNames);
    }

    if (input.invalidateSize && ctx.mapInstance) {
      try {
        ctx.mapInstance.invalidateSize();
      } catch {
        /* ignore */
      }
    }
  }

  private addMarkersToMap(ctx: OsmMapControllerInputContext, markers: Marker[]): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.addMarkers(markers);
  }

  private addSingleMarker(ctx: OsmMapControllerInputContext, marker: Marker): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.addMarker(marker);
  }

  private removeMarkerFromMap(ctx: OsmMapControllerInputContext, markerId: string): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.removeMarker(markerId);
  }

  private removeMarkersFromMap(ctx: OsmMapControllerInputContext, markers: string[]): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.removeMarkers(markers);
  }

  private mergeMarkersToMap(ctx: OsmMapControllerInputContext, markers: Marker[]): void {
    if (!ctx.isMapInitialized || !markers?.length || !ctx.layersController) {
      return;
    }
    ctx.layersController.mergeMarkers(markers);
  }

  private clearAllMarkers(ctx: OsmMapControllerInputContext): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.clearMarkers();
  }

  private addPolygonToMap(
    ctx: OsmMapControllerInputContext,
    coordinates: [number, number][],
    properties?: any,
  ): void {
    if (!ctx.isMapInitialized || !ctx.layersController || !ctx.eventsController) {
      return;
    }
    ctx.layersController.addDynamicPolygon(coordinates, properties, ctx.eventsController);
  }

  private addGisPolygonToMap(ctx: OsmMapControllerInputContext, gisPolygon: any): void {
    if (!ctx.isMapInitialized || !ctx.layersController || !ctx.eventsController) {
      return;
    }
    ctx.layersController.addDynamicPolygon(gisPolygon.coordinates, gisPolygon, ctx.eventsController);
  }

  private addGisPolygonsToMap(ctx: OsmMapControllerInputContext, gisPolygons: any[]): void {
    if (!ctx.isMapInitialized || !gisPolygons || gisPolygons.length === 0) {
      return;
    }
    gisPolygons.forEach(gisPolygon => this.addGisPolygonToMap(ctx, gisPolygon));
  }

  private clearAllPolygons(ctx: OsmMapControllerInputContext): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.clearDynamicPolygons();
  }

  private removePolygonFromMap(ctx: OsmMapControllerInputContext, polygonId: string | number): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.removeDynamicPolygon(polygonId);
  }

  private updatePolygonOnMap(
    ctx: OsmMapControllerInputContext,
    polygonId: string | number,
    coordinates?: [number, number][],
    properties?: any,
  ): void {
    if (!ctx.isMapInitialized || !ctx.layersController) {
      return;
    }
    ctx.layersController.updatePolygon(polygonId, coordinates, properties);
  }

  private setCenterOnMap(ctx: OsmMapControllerInputContext, lat: number, lng: number, zoom?: number): void {
    if (!ctx.isMapInitialized || !ctx.mapInstance) {
      return;
    }
    ctx.mapInstance.setView([lat, lng], zoom || ctx.mapOptions.zoom || 13);
  }

  private fitBoundsToMarkers(ctx: OsmMapControllerInputContext, markers: Marker[]): void {
    if (!ctx.isMapInitialized || !ctx.mapInstance || markers.length === 0) {
      return;
    }
    const bounds = (window as any).L.latLngBounds(markers.map(m => [m.lat, m.lng]));
    ctx.mapInstance.fitBounds(bounds, {padding: [50, 50]});
  }
}
